/**
 * Typeform-style multi-step Connect funnel.
 *
 * Behavior:
 *  - one question per screen, fade between questions
 *  - thin Apple-style progress bar
 *  - keyboard nav: Enter advances, ↑/↓ navigates select options
 *  - autosave to localStorage
 *  - debounced live score preview (no number shown — just a quiet hint when tier rises)
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Loader2 } from 'lucide-react';

import {
  PERSONA_QUESTION_ID,
  Question,
  QUESTIONS,
  personaFromAnswer,
  visibleQuestions,
} from '../data/connectQuestions';
import { previewScore, submitLead } from '../services/leadService';
import type {
  LeadScorePreviewResponse,
  LeadSubmitRequest,
  LeadTier,
} from '../types/lead';

const STORAGE_KEY = 'muleworks.connect.draft';

interface DraftState {
  answers: Record<string, any>;
  index: number;
}

const TIER_ORDER: Record<LeadTier, number> = {
  cold: 0,
  warm: 1,
  hot: 2,
  red_hot: 3,
};

const TIER_HINTS: Record<LeadTier, string | null> = {
  cold: null,
  warm: 'Looking promising…',
  hot: "We're a strong match.",
  red_hot: 'Aaron will want to meet.',
};

function loadDraft(): DraftState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed !== 'object' || !parsed) return null;
    return {
      answers: parsed.answers ?? {},
      index: typeof parsed.index === 'number' ? parsed.index : 0,
    };
  } catch {
    return null;
  }
}

function saveDraft(state: DraftState) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore quota errors */
  }
}

function clearDraft() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

function getUtmParams() {
  if (typeof window === 'undefined') return {};
  const params = new URLSearchParams(window.location.search);
  return {
    utm_campaign: params.get('utm_campaign') || undefined,
    utm_source: params.get('utm_source') || undefined,
    utm_medium: params.get('utm_medium') || undefined,
  };
}

// Loose email shape check. Kept permissive; the backend's pydantic
// EmailStr validator is the real gate. This just keeps obviously-broken
// values ("brian", "test@") from triggering a 422 on submit — we'd
// rather silently drop them than have the visitor get stuck on the
// last question with an error in small red text.
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
function cleanEmail(v: any): string | undefined {
  if (typeof v !== 'string') return undefined;
  const s = v.trim();
  if (!s) return undefined;
  return EMAIL_SHAPE.test(s) ? s : undefined;
}

// Same permissive treatment for URLs — the field is optional and we
// don't want "linkedin.com/in/foo" (no protocol) to 422 the whole submit.
function cleanUrl(v: any): string | undefined {
  if (typeof v !== 'string') return undefined;
  let s = v.trim();
  if (!s) return undefined;
  if (!/^https?:\/\//i.test(s)) s = 'https://' + s;
  try {
    new URL(s);
    return s;
  } catch {
    return undefined;
  }
}

function cleanNonEmpty(v: any): string | undefined {
  if (typeof v !== 'string') return undefined;
  const s = v.trim();
  return s ? s : undefined;
}

function buildSubmitRequest(answers: Record<string, any>): LeadSubmitRequest {
  const persona = personaFromAnswer(answers[PERSONA_QUESTION_ID]);
  const visible = visibleQuestions(answers);

  const responses = visible
    .filter((q) => answers[q.id] !== undefined && answers[q.id] !== '' && answers[q.id] !== null)
    .map((q) => ({
      question_id: q.id,
      question_text: q.text,
      answer: answers[q.id],
    }));

  const utm = getUtmParams();
  return {
    persona,
    responses,
    contact_email: cleanEmail(answers.contact_email),
    contact_name: cleanNonEmpty(answers.contact_name),
    contact_phone: cleanNonEmpty(answers.contact_phone),
    company_name: cleanNonEmpty(answers.company_name),
    linkedin_url: cleanUrl(answers.linkedin_url),
    source: 'connect_funnel',
    ...utm,
  };
}

// ===================================================================
// Question input components
// ===================================================================

interface InputProps {
  question: Question;
  value: any;
  onChange: (v: any) => void;
  onAdvance: () => void;
}

const SingleSelect: React.FC<InputProps> = ({ question, value, onChange, onAdvance }) => {
  const options = question.options ?? [];
  const focusedIdx = options.findIndex((o) => o.value === value);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const dir = e.key === 'ArrowDown' ? 1 : -1;
        const next = ((focusedIdx === -1 ? 0 : focusedIdx) + dir + options.length) % options.length;
        onChange(options[next].value);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [focusedIdx, options, onChange]);

  return (
    <div className="space-y-3">
      {options.map((opt) => {
        const selected = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => {
              onChange(opt.value);
              // small delay so user sees the selection before advancing
              setTimeout(() => onAdvance(), 200);
            }}
            className={`w-full rounded-2xl border px-5 py-4 text-left text-base transition-all ${
              selected
                ? 'border-apple-blue bg-apple-blue/5 text-apple-ink'
                : 'border-apple-divider bg-white text-apple-ink hover:border-apple-ink/40'
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
};

const MultiSelect: React.FC<InputProps> = ({ question, value, onChange }) => {
  const options = question.options ?? [];
  const arr: string[] = Array.isArray(value) ? value : [];
  const toggle = (v: string) => {
    if (arr.includes(v)) onChange(arr.filter((x) => x !== v));
    else onChange([...arr, v]);
  };
  return (
    <div className="space-y-3">
      {options.map((opt) => {
        const selected = arr.includes(opt.value);
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => toggle(opt.value)}
            className={`w-full rounded-2xl border px-5 py-4 text-left text-base transition-all ${
              selected
                ? 'border-apple-blue bg-apple-blue/5 text-apple-ink'
                : 'border-apple-divider bg-white text-apple-ink hover:border-apple-ink/40'
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
};

const TextInput: React.FC<InputProps> = ({ question, value, onChange, onAdvance }) => {
  const inputType =
    question.type === 'email'
      ? 'email'
      : question.type === 'number'
        ? 'number'
        : question.type === 'phone'
          ? 'tel'
          : question.type === 'url'
            ? 'url'
            : 'text';
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    ref.current?.focus();
  }, [question.id]);

  return (
    <input
      ref={ref}
      type={inputType}
      value={value ?? ''}
      onChange={(e) =>
        onChange(question.type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value)
      }
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          onAdvance();
        }
      }}
      placeholder={question.placeholder}
      className="w-full border-0 border-b-2 border-apple-divider bg-transparent pb-3 text-2xl text-apple-ink placeholder:text-apple-subtext/60 focus:border-apple-blue focus:outline-none focus:ring-0"
    />
  );
};

const TextArea: React.FC<InputProps> = ({ question, value, onChange, onAdvance }) => {
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    ref.current?.focus();
  }, [question.id]);
  return (
    <textarea
      ref={ref}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
          e.preventDefault();
          onAdvance();
        }
      }}
      placeholder={question.placeholder}
      rows={4}
      className="w-full resize-none border-0 border-b-2 border-apple-divider bg-transparent pb-3 text-xl text-apple-ink placeholder:text-apple-subtext/60 focus:border-apple-blue focus:outline-none focus:ring-0"
    />
  );
};

const QuestionInput: React.FC<InputProps> = (props) => {
  switch (props.question.type) {
    case 'single_select':
      return <SingleSelect {...props} />;
    case 'multi_select':
      return <MultiSelect {...props} />;
    case 'long_text':
      return <TextArea {...props} />;
    default:
      return <TextInput {...props} />;
  }
};

// ===================================================================
// Page
// ===================================================================

const ConnectPage: React.FC = () => {
  const navigate = useNavigate();
  const initial = useMemo(() => loadDraft() ?? { answers: {}, index: 0 }, []);
  const [answers, setAnswers] = useState<Record<string, any>>(initial.answers);
  const [index, setIndex] = useState<number>(initial.index);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [direction, setDirection] = useState<1 | -1>(1);

  // live score hint state
  const [tierHint, setTierHint] = useState<string | null>(null);
  const lastTierRef = useRef<LeadTier | null>(null);
  const previewTimer = useRef<number | null>(null);

  const visible = useMemo(() => visibleQuestions(answers), [answers]);
  const total = visible.length;
  const safeIndex = Math.min(index, Math.max(total - 1, 0));
  const current: Question | undefined = visible[safeIndex];
  const progress = total === 0 ? 0 : ((safeIndex + 1) / total) * 100;

  // persist draft on every change
  useEffect(() => {
    saveDraft({ answers, index: safeIndex });
  }, [answers, safeIndex]);

  // debounced score preview after each answer change
  useEffect(() => {
    if (!QUESTIONS.find((q) => q.id === PERSONA_QUESTION_ID)) return;
    if (!answers[PERSONA_QUESTION_ID]) return;

    if (previewTimer.current) {
      window.clearTimeout(previewTimer.current);
    }
    previewTimer.current = window.setTimeout(async () => {
      try {
        const req = buildSubmitRequest(answers);
        const res: LeadScorePreviewResponse = await previewScore(req);
        const last = lastTierRef.current;
        if (!last || TIER_ORDER[res.tier] > TIER_ORDER[last]) {
          const hint = TIER_HINTS[res.tier];
          if (hint) {
            setTierHint(hint);
            window.setTimeout(() => setTierHint(null), 2500);
          }
        }
        lastTierRef.current = res.tier;
      } catch {
        // silently ignore — preview is non-critical
      }
    }, 500);

    return () => {
      if (previewTimer.current) window.clearTimeout(previewTimer.current);
    };
  }, [answers]);

  const setAnswer = useCallback((id: string, val: any) => {
    setAnswers((prev) => ({ ...prev, [id]: val }));
  }, []);

  const isAnswered = (q: Question | undefined): boolean => {
    if (!q) return false;
    if (q.optional) return true;
    const v = answers[q.id];
    if (q.type === 'multi_select') return Array.isArray(v) && v.length > 0;
    return v !== undefined && v !== null && v !== '';
  };

  const goNext = useCallback(async () => {
    if (!current) return;
    if (!isAnswered(current)) return;

    if (safeIndex < total - 1) {
      setDirection(1);
      setIndex(safeIndex + 1);
      return;
    }

    // last question — submit
    setSubmitting(true);
    setError(null);
    try {
      const req = buildSubmitRequest(answers);
      const res = await submitLead(req);
      sessionStorage.setItem('muleworks.connect.result', JSON.stringify(res));
      clearDraft();
      navigate('/connect/result');
    } catch (e: any) {
      setError(
        e?.response?.data?.detail ||
          e?.message ||
          "We couldn't submit just yet — please try again.",
      );
    } finally {
      setSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, safeIndex, total, answers, navigate]);

  const goPrev = () => {
    if (safeIndex > 0) {
      setDirection(-1);
      setIndex(safeIndex - 1);
    }
  };

  // global Enter key advances on text/select-completed states (single_select handles its own auto-advance)
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Enter') return;
      if (!current) return;
      // only intercept when not in a textarea (so newlines work)
      const tag = (e.target as HTMLElement | null)?.tagName?.toLowerCase();
      if (tag === 'textarea') return;
      if (current.type === 'single_select') return; // auto-advances
      if (isAnswered(current)) {
        e.preventDefault();
        void goNext();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, answers, goNext]);

  if (!current) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-apple-bg text-apple-subtext">
        Loading…
      </div>
    );
  }

  const isLast = safeIndex === total - 1;

  return (
    <div className="flex min-h-screen flex-col bg-apple-bg font-sans antialiased">
      {/* Progress bar */}
      <div className="fixed inset-x-0 top-0 z-20 h-1 bg-apple-divider/40">
        <motion.div
          className="h-full bg-apple-blue"
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
        />
      </div>

      {/* Top nav */}
      <header className="fixed inset-x-0 top-1 z-10 flex h-12 items-center justify-between px-6">
        <button
          type="button"
          onClick={goPrev}
          disabled={safeIndex === 0}
          className={`inline-flex items-center gap-1 text-sm transition-colors ${
            safeIndex === 0
              ? 'invisible'
              : 'text-apple-subtext hover:text-apple-ink'
          }`}
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
        <span className="text-xs text-apple-subtext">
          {safeIndex + 1} / {total}
        </span>
      </header>

      {/* Question */}
      <main className="relative flex flex-1 items-center justify-center px-6 pt-20">
        <div className="w-full max-w-2xl">
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={current.id}
              custom={direction}
              initial={{ opacity: 0, y: direction === 1 ? 20 : -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: direction === 1 ? -20 : 20 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            >
              <h1 className="text-3xl font-semibold tracking-tight text-apple-ink sm:text-4xl">
                {current.text}
                {current.optional && (
                  <span className="ml-2 align-middle text-sm font-normal text-apple-subtext">
                    (optional)
                  </span>
                )}
              </h1>
              {current.subtitle && (
                <p className="mt-3 text-apple-subtext">{current.subtitle}</p>
              )}

              <div className="mt-10">
                <QuestionInput
                  question={current}
                  value={answers[current.id]}
                  onChange={(v) => setAnswer(current.id, v)}
                  onAdvance={() => void goNext()}
                />
              </div>

              <div className="mt-10 flex items-center justify-between">
                <div className="text-xs text-apple-subtext">
                  {current.type === 'long_text'
                    ? 'Press ⌘ + Enter to continue'
                    : 'Press Enter to continue'}
                </div>
                <button
                  type="button"
                  onClick={() => void goNext()}
                  disabled={submitting || (!isAnswered(current) && !current.optional)}
                  className={`inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium transition-colors ${
                    submitting || (!isAnswered(current) && !current.optional)
                      ? 'cursor-not-allowed bg-apple-divider text-apple-subtext'
                      : 'bg-apple-blue text-white hover:bg-apple-blueHover'
                  }`}
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Submitting…
                    </>
                  ) : isLast ? (
                    'Finish'
                  ) : (
                    <>
                      Continue
                      <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </button>
              </div>

              {error && (
                <div
                  role="alert"
                  className="mt-6 rounded-xl border border-red-200 bg-red-50 p-4"
                >
                  <p className="text-sm font-medium text-red-800">
                    We couldn't submit that.
                  </p>
                  <p className="mt-1 text-sm text-red-700">{error}</p>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Quiet tier hint */}
        <AnimatePresence>
          {tierHint && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 8 }}
              transition={{ duration: 0.4 }}
              className="pointer-events-none fixed bottom-8 left-1/2 -translate-x-1/2 rounded-full bg-apple-ink/85 px-4 py-2 text-xs text-white shadow-lg"
            >
              {tierHint}
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
};

export default ConnectPage;
